import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Building2, History, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Past Event Card - No purchase buttons, no countdown
const PastEventCard = ({ event }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const genreLabels = {
    opera: 'Ópera',
    ballet: 'Ballet',
    concierto: 'Concierto',
    teatro: 'Teatro',
    musical: 'Musical',
    cine: 'Cine'
  };

  return (
    <Link
      to={`/evento/${event.id}`}
      className="card-event block group"
      data-testid={`past-event-card-${event.id}`}
    >
      {/* Image with grayscale effect */}
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={event.image_url}
          alt={event.title}
          className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        
        {/* Badge - Past Event */}
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-white/20 text-white text-xs px-2 py-0.5 uppercase tracking-wider font-medium backdrop-blur-sm">
            Evento Pasado
          </span>
          <span className="bg-[var(--secondary)] text-white text-xs px-2 py-0.5 uppercase tracking-wider font-medium">
            {genreLabels[event.genre] || event.genre}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-white group-hover:text-[var(--primary)] transition-colors duration-300">
          {event.title}
        </h3>

        <p className="text-white/50 mt-3 line-clamp-2 text-sm">
          {event.description}
        </p>

        <div className="mt-4 flex flex-wrap gap-3 text-sm text-white/50">
          <span className="flex items-center gap-1">
            <Calendar size={14} className="text-white/40" />
            {formatDate(event.date)}
          </span>
          <span className="flex items-center gap-1">
            <Building2 size={14} className="text-white/40" />
            {event.venue}
          </span>
        </div>
      </div>
    </Link>
  );
};

export const PastEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPastEvents();
  }, []);

  const fetchPastEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/past`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching past events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="past-events-page">
      {/* Header */}
      <div className="section-container py-12 border-b border-white/5">
        <Link to="/catalogo" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-6">
          <ArrowLeft size={18} />
          <span className="text-xs uppercase tracking-widest">Volver al catálogo</span>
        </Link>
        
        <div className="flex items-center gap-4 mb-4">
          <History size={32} className="text-[var(--primary)]" />
          <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em]">
            Archivo
          </span>
        </div>
        <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl text-white mb-6">
          Eventos Pasados
        </h1>
        <p className="text-white/50 max-w-2xl">
          Revive los mejores momentos de nuestros eventos anteriores. Aquí encontrarás el archivo completo de producciones que ya se han celebrado.
        </p>
      </div>

      {/* Events Grid */}
      <div className="section-container section-spacing">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="card-event">
                <div className="aspect-[4/3] skeleton" />
                <div className="p-6 space-y-4">
                  <div className="h-6 skeleton w-3/4" />
                  <div className="h-4 skeleton w-full" />
                  <div className="h-4 skeleton w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="past-events-grid">
            {events.map((event) => (
              <PastEventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-past-events">
            <History size={64} className="text-white/20 mx-auto mb-4" />
            <p className="text-white/50 text-lg mb-4">
              Aún no hay eventos pasados en el archivo.
            </p>
            <Link to="/catalogo" className="btn-outline">
              Ver próximos eventos
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastEvents;
